import { useState, useEffect, useCallback, useRef } from "react";
import { apiJson } from "../api/apiClient";

const POLL_MS = 8000; // poll every 8 seconds

export function useMessages(tenant, user) {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isAdminTyping, setIsAdminTyping] = useState(false);

  const lastIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const isOpenRef = useRef(false);
  const prevUserIdRef = useRef(null);

  const token = user?.token ?? null;

  // apiJson already sets Accept/Content-Type; this only needs to add auth.
  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // ── Load full conversation (called when panel opens) ───────────────────────
  const loadMessages = useCallback(async () => {
    if (!token || !tenant) return;
    setLoading(true);
    try {
      const data = await apiJson(tenant, "/messages", {
        headers: authHeaders(),
      });
      const list = data.messages || [];
      setMessages(list);
      setUnread(0);
      if (list.length > 0) {
        lastIdRef.current = list[list.length - 1].id;
      }
    } catch (_) {}
    setLoading(false);
  }, [token, tenant, authHeaders]);

  const poll = useCallback(async () => {
    if (!token || !tenant) return;
    try {
      if (isOpenRef.current) {
        const path = `/messages${lastIdRef.current ? `?after=${lastIdRef.current}` : ""}`;
        const data = await apiJson(tenant, path, { headers: authHeaders() });

        // ── Read typing state from same response ──────────────
        setIsAdminTyping(data.admin_typing === true);

        const newMsgs = (data.messages || []).filter(
          (m) => !lastIdRef.current || m.id > lastIdRef.current,
        );
        if (newMsgs.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const toAdd = newMsgs.filter((m) => !existingIds.has(m.id));
            if (!toAdd.length) return prev;
            lastIdRef.current = Math.max(...toAdd.map((m) => m.id));
            return [...prev, ...toAdd];
          });
          // New message arrived → admin stopped typing
          setIsAdminTyping(false);
        }
      } else {
        const data = await apiJson(tenant, "/messages/unread", {
          headers: authHeaders(),
        });
        setUnread(data.unread || 0);
      }
    } catch (_) {}
  }, [token, tenant, authHeaders]);

  // ── Send a message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || !token || !tenant) return false;
      setSending(true);
      try {
        const data = await apiJson(tenant, "/messages/send", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ message: text.trim() }),
        });
        if (data.message) {
          setMessages((prev) => {
            if (prev.find((m) => m.id === data.message.id)) return prev;
            lastIdRef.current = data.message.id;
            return [...prev, data.message];
          });
          setSending(false);
          return true;
        }
      } catch (_) {}
      setSending(false);
      return false;
    },
    [token, tenant, authHeaders],
  );

  // ── Fetch unread count only ────────────────────────────────────────────────
  const fetchUnread = useCallback(async () => {
    if (!token || !tenant) return;
    try {
      const data = await apiJson(tenant, "/messages/unread", {
        headers: authHeaders(),
      });
      setUnread(data.unread || 0);
    } catch (_) {}
  }, [token, tenant, authHeaders]);

  // ── Mark panel as open/closed ─────────────────────────────────────────────
  const setOpen = useCallback((open) => {
    isOpenRef.current = open;
  }, []);

  // ── Reset state on user change (logout / account switch) ──────────────────
  useEffect(() => {
    const currentId = user?.id ?? null;
    if (prevUserIdRef.current !== null && prevUserIdRef.current !== currentId) {
      setMessages([]);
      setUnread(0);
      setIsAdminTyping(false);
      lastIdRef.current = null;
      isOpenRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
    prevUserIdRef.current = currentId;
  }, [user?.id]);

  // ── Start polling on mount, stop on unmount ────────────────────────────────
  useEffect(() => {
    if (!token || !tenant) {
      setMessages([]);
      setUnread(0);
      lastIdRef.current = null;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }

    fetchUnread();
    pollTimerRef.current = setInterval(poll, POLL_MS);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [token, tenant, poll, fetchUnread]);

  return {
    messages,
    unread,
    loading,
    sending,
    isAdminTyping,
    loadMessages,
    sendMessage,
    fetchUnread,
    setUnread,
    setOpen,
  };
}
